import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { Badge } from '@abyss/web/ui/Badge';
import { Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { BreadcrumbSessionStorage } from '#/models/BreadcrumbSessionStorage';
import find from 'lodash/find';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import {
  cardContainerBorderCss,
  cardContainerHeightCss,
  cardHeightForSmartChoice,
  conditionalHandler,
  renderWidth,
  shouldShowDocASAP,
} from '../../frontends/ProviderSearch/routes/MapView/utils';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { useSpecialtiesResult } from '../../hooks/useSpecialties';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { BhProviderIcons, CustomPreferredPolicy } from '../../models/Lagoon';
import { FacilityDDPCode, Provider } from '../../models/Provider';
import {
  Address as ProviderAddress,
  ProviderType,
  RecommendationDetails,
} from '../../models/ProviderDetails';
import { ProviderSearchFilters } from '../../models/ProviderSearch';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../store/useAnalyticsStore/analyticsStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../store/useDetailsStore';
import { DetailsStore } from '../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { usePlanSelectorStore } from '../../store/usePlanSelectorStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { useVirtualCareStore } from '../../store/useVirtualCareStore';
import { getSpecialityTitle } from '../../utils/card.utils';
import {
  checkAscensionScheduleDataIndicator,
  getPlanIndicator,
} from '../../utils/common';
import { isProviderStartDateInFuture } from '../../utils/date.utils';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { getBhIcon } from '../../utils/iconography.utils';
import { clearRoute, setResultIcon } from '../../utils/map.utils';
import {
  displayPreferredFacilityOnCards,
  getPremiumCarePhysician,
  getProviderDetailsRouteUrl,
  getProviderDetailsUrl,
  getSuppressFacilityFlags,
  isShowPreferredFacilityBadge,
} from '../../utils/providerDetails.utils';
import {
  getAutoIncrementRadius,
  shouldSuppressAddressAndMiles,
} from '../../utils/providerSearch.utils';
import { getShowCost } from '../../utils/showCost.utils';
import { removeLeadingZeroes } from '../../utils/string.utils';
import {
  getCurrentMember,
  getIsCustomPreferredPolicy,
  getIsOxfordAndUSP,
  getLoggedInMember,
  getMarketType,
  getNetworkIdsForPES,
  getProductCodeForCoverageType,
} from '../../utils/user.utils';
import { isVirtualVisitOnlyAddress } from '../../utils/virtualVisitOnly';
import { adobeSearchResultClickEvent } from '../AdobeTagging/adobeSearchResultClickEvent';
import { BookmarkButton } from '../BookmarkButton/BookmarkButton';
import {
  Constants,
  ReverseCoverageTypesCodes,
  ReverseDentalCodes,
} from '../Constants';
import { FeatureFlags } from '../ConstantsFeatureFlags';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import {
  CardButtonGroupedLocationStyle,
  CardButtonStyle,
  CardButtonViewListStyle,
  ProviderMetaFontSizeStyle,
  SmartChoiceFontSizeStyle,
  mobileOnly,
  notXlScreenOnly,
  phoneOnly,
  tabletScreen,
} from '../ConstantsStyles';
import { ImageComponent } from '../ImageComponent';
import { InNetworkBadgeWithDate } from '../InNetworkBadge/InNetworkBadge';
import { PortalName } from '../Portal';
import {
  defaultStringToEmpty,
  getAcoBenefitPlan,
} from '../PSXHeader/SmartChoice/utils';
import { PSXIcon } from '../PSXIcon';
import {
  CardInfo,
  providerPreferredHeading,
  providerPreferredIcon,
} from '../SnackCard/CardInfo';
import { CardInfoForUMR } from '../SnackCard/CardInfoForUMR';
import { SmartChoiceScoreCard } from '../SnackCard/SmartChoiceScoreCard';
import { TerminationStatusIndicator } from '../TerminationBadge/TerminationStatusIndicator';
import { convertObjectToUrlParams, valCheck } from '../Utils';
import {
  appendCoverageTypes,
  convertProviderTypeToAdobeType,
  formatProviderId,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { getBccBadgeState } from '../Utils/bccBadgeUtils';
import { getTierProviderEligibility } from '../Utils/memberUtils/memberUtils';
import { shouldWrapProviderNameText } from '../Utils/shouldWrapProviderNameText';
import { checkTerminationDate } from '../Utils/terminateDateIndicatorUtils';
import { truncateLargeStrings } from '../Utils/truncateLargeStrings';
import { Address } from './Address';
import { ChoosePCP } from './ChoosePCP';
import { CostIndicatorPopover } from './CostIndicatorPopover';
import {
  AvatarDataCard,
  CardContainer,
  DataCardAvatarContainer,
  FacilityTagStyle,
  FacilityTextStyle,
  FlexStyles,
  ImageFlexContainer,
  InNetworkBadgeContainer,
  MapPopupPremiumCareCardInfo,
  MilesAndAddressWrapper,
  PopupContainerStyle,
  PrimarySectionStyle,
  ResponsiveTextSpeciality,
  ResultCard,
  SecondarySectionStyle,
  SmartChoiceListViewAvatar,
  SnackRole,
  TerminationDateIndicatorContainer,
  VirtualVisitLabel,
  VirtualVisitPatientStyle,
  VirtualVisitPatientStyleGroupedLocation,
  VirtualVisitStyle,
  VoiceOnlyText,
  cardBookmarkButtonStyles,
  cardSectionStyle,
  costIndicatorStyles,
  displayVirtualVisitStyle,
  flexSpecialityAndBadgeStyle,
  getFlexJustification,
  getTextSize,
  mileAndAddressGap,
  milesTextStyles,
  nameAndBadgeStyle,
  networkDisplayStyles,
  popupSecondarySectionStyle,
  popupSpecialityNameToolTipStyles,
  providerNameListViewToolTipStyles,
  specialityNameToolTipStyles,
  virtualVistsToolTipStyles,
} from './DataCard.style';
import {
  displayPremiumCareIdentifier,
  displayTier1Identifier,
  displayTier2Identifier,
  getIsSmartChoicePriceCheck,
  getLinkNameForSearchResultEvent,
  getLinkPositionIndex,
  handlePatientReviewsView,
  inOrOutOfNetwork,
  infoCardHeight,
  isIFPOrganizationCheck,
  isPremiumCareDisplayEligible,
  ratingsOnClickHandler,
  shouldDisplayVirtualVisit,
  shouldShowCursorOnRatings,
  shouldSuppressVirtualCare,
} from './DataCardUtils';
import { Miles } from './Miles';
import { milesAndAddressStyles } from './Miles.style';
import { NameHeading } from './NameHeading';
import { NetworkDisplay } from './NetworkDisplay';
import { ProviderRating } from './ProviderRating';
import { additionalRatingStyle } from './ProviderRating/ProviderRating.styles';

const displayVirtualVisitAddress = (
  isVirtualCareQuickCareSuppressed,
  value,
  address
) =>
  isVirtualCareQuickCareSuppressed &&
  isVirtualVisitOnlyAddress(value, address?.line[0] as string);

export const renderBookmarkButton = (
  tablet: boolean,
  expandedProviderListView: boolean | undefined,
  locationId: string,
  providerId: string,
  providerType: string | undefined,
  enableCareTeamView: boolean,
  providerName?: string
) => {
  if (expandedProviderListView) {
    return (
      <Flex
        css={{
          alignItems: 'center',
          height: tablet ? '24px' : '',
          paddingBottom: tablet ? '24px' : '',
          width: tablet ? '131px' : '',
        }}
        justify="flex-end"
      >
        {enableCareTeamView && (
          <BookmarkButton
            expandedProviderListView={expandedProviderListView}
            fromDataCard={true}
            locationId={locationId}
            providerId={providerId}
            providerName={providerName}
            providerType={providerType}
          />
        )}
      </Flex>
    );
  }
  return null;
};

const truncateSpecialityTitle = (
  isGroupedLocationDrawer,
  specialityTitleText,
  notXlScreen
) =>
  isGroupedLocationDrawer
    ? truncateLargeStrings(specialityTitleText, notXlScreen ? 20 : 40)
    : specialityTitleText;

const getPrimarySectionStyle = (popupContainer, isNewMapViewDataCardDesign) =>
  popupContainer
    ? PopupContainerStyle
    : PrimarySectionStyle(isNewMapViewDataCardDesign);

type Props = {
  popupContainer?: any;
  practitioner?: Provider;
  surveyCount?: number;
  updatePin?: (practitioner?: Provider) => void;
  handleDirectionClick?: () => void;
  index?: number;
  enableCost?: boolean;
  pageNumber?: number;
  map?: any;
  selectedFilters?: string;
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  usedAt: string;
  searchTerm?: string;
  setNavigateToDirections?: (a: boolean) => void;
  headers: ResponseHeaders;
  providerType?: string;
  linkLocationName: string;
  sectionTypeDerived: string;
  providerId: string;
  locationId: string;
  isTieredProvider: boolean;
  speciality?: string;
  setSearchFilters: (a: ProviderSearchFilters) => void;
  searchFilters: ProviderSearchFilters;
  setSelectedId: (a: string | null) => void;
  setHighlightId: (a: { providerId: string; from: string }) => void;
  highlightId: { providerId: string; from: string };
  showBestMatchDetails: boolean;
  setIsOpen: (a: boolean) => void;
  virtualCareIndicatorEnabled: boolean;
  cardRef: React.RefObject<HTMLElement>;
  highlightClassName: string;
  cardContainerCss: any;
  handleHoverOnCard: (a: any) => void;
  handleExitOnCard: (a: any) => void;
  imgRef: any;
  name: string;
  providerSpeciality: string;
  suppressCostEstimates: boolean;
  suppressPatientReviews: boolean;
  healthGradeRating?: string;
  networkStatus?: string;
  npi: string;
  distance?: string;
  address?: ProviderAddress;
  primaryPhone: string;
  choosePCP: boolean;
  docasapPracticeId: string;
  organizationCode?: string[];
  networkEndDate?: string;
  isNetworkAlertIndicator?: boolean;
  isMNR?: boolean;
  networkStartDate?: string;
  isInNetworkBadge?: boolean;
  enableMapEnhancements?: boolean;
  isPopup?: boolean;
  showSmartChoice?: boolean;
  isTieredPlan?: boolean;
  acoProvider?: boolean;
  handleSmartChoiceClick?: () => void;
  showInternalCardInfo?: boolean;
  badgeCardType?: string;
  searchRadius?: number;
  expandedProviderListView?: boolean;
  enableMapViewHideOption?: boolean;
  providerList?: Provider[];
  isGroupedLocationDrawer?: boolean;
  currentPage?: number;
  drawerContent?: any;
  isCheckboxDisabled?: any;
  setSelectedCheckbox?: any;
  addSelectedProvider?: any;
  adobeLinkTrackEventCheckBox?: any;
  setLastSelectedIndex?: any;
  selectedCheckbox?: any;
  compareShareToggle?: boolean;
  setIsCompareClicked?: any;
  maxBadgeCount?: number;
  networkId?: string;
  isNetworkInfo?: boolean;
  recommendationPreferences?: any;
  virtualIndicator?: any;
};

export const DataCard = ({
  searchTerm,
  selectedFilters,
  index,
  providerType,
  popupContainer,
  linkLocationName,
  headers,
  practitioner,
  sectionTypeDerived,
  providerId,
  locationId,
  isTieredProvider,
  speciality,
  setSearchFilters,
  searchFilters,
  pageNumber,
  handleDirectionClick,
  usedAt,
  setNavigateToDirections,
  setSelectedId,
  map,
  setHighlightId,
  updatePin,
  setRouteEndCoords,
  highlightId,
  showBestMatchDetails,
  setIsOpen,
  virtualCareIndicatorEnabled,
  cardRef,
  highlightClassName,
  cardContainerCss,
  handleHoverOnCard,
  handleExitOnCard,
  imgRef,
  name,
  providerSpeciality,
  suppressCostEstimates,
  suppressPatientReviews,
  healthGradeRating,
  networkStatus,
  npi,
  enableCost,
  distance,
  address,
  primaryPhone,
  choosePCP,
  docasapPracticeId,
  organizationCode,
  networkEndDate,
  isNetworkAlertIndicator,
  networkStartDate,
  isInNetworkBadge,
  enableMapEnhancements,
  isPopup,
  showSmartChoice,
  handleSmartChoiceClick,
  isTieredPlan = false,
  acoProvider = false,
  showInternalCardInfo = false,
  badgeCardType = 'data-card',
  searchRadius,
  expandedProviderListView,
  enableMapViewHideOption = false,
  providerList,
  isGroupedLocationDrawer = false,
  currentPage,
  drawerContent,
  isCheckboxDisabled,
  setSelectedCheckbox,
  addSelectedProvider,
  adobeLinkTrackEventCheckBox,
  setLastSelectedIndex,
  selectedCheckbox,
  compareShareToggle,
  setIsCompareClicked,
  maxBadgeCount,
  networkId,
  isNetworkInfo = false,
  recommendationPreferences,
  virtualIndicator,
}: Props) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const mobileScreen = useMediaQuery(phoneOnly);
  const tablet = useMediaQuery(`(min-width: 744px) and (max-width: 1247px)`);
  const { portalData } = useContext(PortalContext);
  const portalShowCost = getShowCost(portalData);
  const currentMember = getCurrentMember();
  const [showDirection, setShowDirection] = useState(false);
  const [searchParams] = useSearchParams();
  const [breadcrumbSessionStorage] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );
  const isUmr = portalData?.portalName === PortalName.UMR;

  const addressMilesRef = useRef<HTMLDivElement>(null);
  const platinumBadgeRef = useRef<HTMLDivElement>(null);
  const premiumCareBadgeRef = useRef<HTMLDivElement>(null);
  const tier1BadgeRef = useRef<HTMLDivElement>(null);
  const tier2BadgeRef = useRef<HTMLDivElement>(null);
  const patientRatingStarsRef = useRef<HTMLDivElement>(null);
  const costIndicatorRef = useRef<HTMLDivElement>(null);
  const preferredProviderRef = useRef<HTMLDivElement>(null);
  const preferredFacilityRef = useRef<HTMLDivElement>(null);

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const {
    chipValue: value,
    coverageType: coverageTypeValue,
    setChipValue: setStoreChipValue,
    setCoverageType: setStoreCoverageType,
  } = chipStore;

  const [shouldWrapName, setShouldWrapName] = useState<boolean>(false);

  const planInfo = usePlanSelectorStore((state) => state.planInfo);

  useEffect(() => {
    const shouldWrap = shouldWrapProviderNameText(
      platinumBadgeRef?.current,
      premiumCareBadgeRef?.current,
      tier1BadgeRef?.current,
      tier2BadgeRef?.current,
      patientRatingStarsRef?.current,
      costIndicatorRef?.current,
      preferredProviderRef?.current,
      preferredFacilityRef?.current
    );
    if (shouldWrap) {
      setShouldWrapName(true);
    } else {
      setShouldWrapName(false);
    }
  }, []);

  const featureFlags = useLagoon('feature-flags')();

  const [
    isDentalLocationNameEnable,
    enableCareTeamView,
    schedulingEnabled,
    displayInNetworkBadgeEnabled,
    ctaDisplayLogic,
    isPlatinumProviderIdentifierEnabled,
    isMultipleBadgeEnabled,
    displayTerminationDateEnabled,
    isLimitedAccessToProvider,
    isSchedulingWithFacilityEnabled,
    preferredFacilityFlag,
    oxfordPlnFlag,
    enablePCPParamsFromURL,
    enablePlanTypeSelectionInFilter,
    showTierOneFlag,
    isMultipleBadgeCardViewEnabled,
    enableVirtualCareEpic,
    ascesionIndicatorEnabled,
    enableBccBadge,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_DENTAL_LOCATION_NAME,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CARE_TEAM_VIEW,
    ConstantsLagoon.FEATURE_FLAGS.ONLINE_SCHEDULING,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_IN_NETWORK_BADGE_FUTURE_START_DATE,
    ConstantsLagoon.FEATURE_FLAGS.PSX_CTA_DISPLAY_LOGIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROVIDER_IDENTIFIER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MULTIPLE_BADGE_DATA_CARD_VIEW,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE,
    ConstantsLagoon.FEATURE_FLAGS
      .LIMITED_ACCESS_TO_PROVIDER_BANNER_AND_SCHEDULE,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_SCHEDULING_WITH_FACILITY_LIMITED_ACCESS_PROVIDERS,
    ConstantsLagoon.FEATURE_FLAGS.PREFERRED_FACILITY_IDENTIFICATION,
    ConstantsLagoon.FEATURE_FLAGS.OXFORD_PLN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PCP_PARAMS_FROM_URL,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLAN_TYPE_SELECTION_IN_FILTER,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ].TIER_ONE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MULTIPLE_BADGE_DATA_CARD_VIEW,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ASCENSION_INDICATOR_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROVIDER_BCC_BADGE,
  ]);

  const isPlatinumProvider = practitioner?.isPlatinumProvider;

  const isGreenDiamondIndicator = practitioner?.isGreenDiamondProvider;

  const uiMessagingData = useLagoon('ui-messaging')();

  const doShowDocASAP = shouldShowDocASAP(schedulingEnabled, docasapPracticeId);

  const getBadgeSize = () => (mobileScreen ? 16 : 20);
  const badgeSize = getBadgeSize();

  const isCurrentMemberPreEffective = useCurrentMemberIsPreEffective();
  const notXlScreen = useMediaQuery(notXlScreenOnly);
  const smallScreen = useMediaQuery(mobileOnly);
  const TabletScreen = useMediaQuery(tabletScreen);

  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );

  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );

  const { choosePCP: choosePCPFlag, setPCPSearchState } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
      setPCPSearchState: state.setPCPSearchState,
    }))
  );

  const handleAdobeSearchResultClickEvent = (clickSection?: string) => {
    adobeSearchResultClickEvent({
      filters: selectedFilters,
      linkName: getLinkNameForSearchResultEvent(providerType, clickSection),
      linkPosition: getLinkPositionIndex(index),
      linkLocation: linkLocationName,
      customAttributesBlock: {
        correlationId: headers.correlationId,
        providerId: formatProviderId(
          appendCoverageTypes(practitioner, coverageType)
        ),
        providerType: convertProviderTypeToAdobeType(providerType),
      },
      term: searchTerm,
    });
  };

  const getBhIconName = (): string => {
    if (coverageType !== 'B') return '';
    return getBhIcon(
      [],
      [providerSpeciality],
      bhProviderIcons,
      bhSpecialtyCodes
    );
  };

  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const { planToken } = getPlanSelectorVariables();

  const coverageType = coverageTypeValue;
  const bhProviderIcons: BhProviderIcons[] = useLagoon('bh_provider_icons')();
  const { data: bhSpecialtyCodes = [] } = useSpecialtiesResult({
    coverageType: 'B',
  });
  const bhIcon = getBhIconName();
  const isNameSearch = searchParams.get('searchType') === 'name';
  const isNotSpecialitySearch =
    searchParams.get('searchBySpeciality') === 'false';

  const setLastSelectedCardSessionStorage = (key, value) => {
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.LAST_SELECTED_FROM_DRAWER,
      {
        providerId,
        currentPage,
        drawerContent,
        breadcrumbs: {
          [ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key]:
            breadcrumbSessionStorage[
              ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key
            ],
          [key]: value,
        },
      }
    );
  };
  const handleDetailsOnClick = ({ showDirection }) => {
    handleAdobeSearchResultClickEvent();
    const typeaheadStoreValues = {
      sectionType: sectionTypeDerived,
      searchTerm,
    };
    const detailsStoreValues = {
      providerId,
      locationId,
      showDirection,
      isTieredDataCard: isTieredProvider,
      specialtySearch: speciality,
      organizationCode,
    };
    setStoreChipValue(value);
    setStoreCoverageType(coverageType);

    setDetailsStore(detailsStoreValues);
    setTypeaheadSearchStore(typeaheadStoreValues);
    setSearchFilters({
      ...searchFilters,
      PageNumber: pageNumber,
    });

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_ID,
      providerId
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.IS_PROVIDER_CARD_SELECTED,
      true
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_SPECIALTY,
      speciality
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_FACILITY_LOCATION_ID,
      locationId
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.PROVIDER_LIST,
      providerList
    );
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_INDEX,
      index
    );
    headers?.correlationId &&
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
        headers?.correlationId
      );

    const providerTierDesignation =
      practitioner?.recommendationDetails?.benefits?.providerTierDesignation;
    const llmStatus = practitioner?.recommendationDetails?.quality?.llmStatus;
    const updatedSearchRadius = searchFilters?.VirtualCare?.value
      ? autoIncrementRadiusArr?.[autoIncrementRadiusArr?.length - 1]
      : searchRadius;
    const providerDetailsRoute = getProviderDetailsRouteUrl(
      chipStore,
      providerId,
      locationId,
      showDirection,
      isTieredProvider,
      sectionTypeDerived,
      choosePCP,
      isTieredPlan,
      acoProvider,
      practitioner?.llmCodes,
      updatedSearchRadius,
      isNameSearch || isNotSpecialitySearch,
      providerTierDesignation,
      llmStatus,
      enablePlanTypeSelectionInFilter ? networkId : undefined,
      recommendationPreferences?.suppressPremium,
      recommendationPreferences?.suppressCostEfficiency
    );

    if (isGroupedLocationDrawer) {
      setLastSelectedCardSessionStorage(
        ConstantsRoutes.PROVIDER_DETAILS.key,
        providerDetailsRoute
      );
    }
    navigate(providerDetailsRoute);
  };

  const showDataDirection = () => {
    if (handleDirectionClick) {
      if (usedAt === Constants.MOBILE_LIST_VIEW) {
        setNavigateToDirections?.(true);
        setSelectedId(locationId || null);
      } else {
        setNavigateToDirections?.(true);
        handleDirectionClick();
      }
    } else {
      setNavigateToDirections?.(true);
      handleDetailsOnClick({ showDirection: true });
      setShowDirection(true);
    }
  };
  const handleOnHeadingBlur = () => {
    if (map?.current) {
      setResultIcon(
        map.current,
        +providerId,
        false,
        setHighlightId,
        providerId
      );
      updatePin?.(undefined);
    }
    setHighlightId({ providerId: '', from: usedAt });
  };

  const handleNavigationOnClick = () => {
    if (map?.current && setRouteEndCoords) {
      clearRoute(
        map.current,
        setRouteEndCoords,
        highlightId.providerId,
        setHighlightId
      );
      setSelectedId(null);
    }
    showDataDirection();
  };

  const onCardClick = () => {
    if (showBestMatchDetails) {
      handleAdobeSearchResultClickEvent('datacard click');
      setIsOpen(true);
    } else {
      handleDetailsOnClick({ showDirection: false });
      setShowDirection(false);
    }
  };
  const handleChoosePCPClick = (event) => {
    adobeSearchResultClickEvent({
      linkName: 'choose-pcp-button',
      linkPosition: getLinkPositionIndex(index),
      linkLocation: linkLocationName,
      customAttributesBlock: {
        ...(isSmartChoicePractitioner && {
          smartChoiceScore: `smart choice score: ${practitioner?.recommendationDetails?.totalRecommendationScore}`,
        }),
        correlationId: headers.correlationId,
        providerId: formatProviderId(
          appendCoverageTypes(practitioner, coverageType)
        ),
        providerType: convertProviderTypeToAdobeType(providerType),
      },
    });

    setAnalyticsState({
      originLinkNameForAnalytics: 'choose-pcp-button',
    });

    if (enablePCPParamsFromURL) {
      const pcpStoreValues = {
        choosePCPId: event.currentTarget.dataset.providerid,
        selectedProviderType: providerType,
      };
      setPCPSearchState(pcpStoreValues);

      const urlParams = convertObjectToUrlParams(chipStore, {
        ...pcpStoreValues,
        coverageType,
      });
      if (isGroupedLocationDrawer) {
        const choosePcpRoute =
          ConstantsRoutes.CHOOSE_PCP_LOCATION.path + urlParams;
        setLastSelectedCardSessionStorage(
          ConstantsRoutes.CHOOSE_PCP_LOCATION.key,
          choosePcpRoute
        );
      }
      navigate(`${ConstantsRoutes.CHOOSE_PCP_LOCATION.path}${urlParams}`);
      return;
    }
    const pcpStoreValues = {
      choosePCPId: event.currentTarget.dataset.providerid,
      selectedProviderType: providerType,
      choosePCP: choosePCPFlag,
      imageLocation: imgSrc,
    };
    setPCPSearchState(pcpStoreValues);

    const urlParams = convertObjectToUrlParams(chipStore, {
      ...pcpStoreValues,
      value,
      coverageType,
    });

    if (isGroupedLocationDrawer) {
      const choosePcpRoute =
        ConstantsRoutes.CHOOSE_PCP_LOCATION.path + urlParams;
      setLastSelectedCardSessionStorage(
        ConstantsRoutes.CHOOSE_PCP_LOCATION.key,
        choosePcpRoute
      );
    }
    navigate(`${ConstantsRoutes.CHOOSE_PCP_LOCATION.path}${urlParams}`);
  };
  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare = shouldSuppressVirtualCare(
    isVirtualCareQuickCareSuppressed,
    coverageType,
    isVirtualCareUnetRailroadSuppressed
  );

  const isIFPOrganization = isIFPOrganizationCheck(
    providerType,
    currentMember?.isIFPMember
  );

  const displayVirtualVisit = shouldDisplayVirtualVisit(
    virtualCareIndicatorEnabled,
    suppressVirtualCare,
    practitioner?.address,
    isIFPOrganization,
    !!practitioner?.virtualCareOffered?.length,
    providerType
  );

  const imgSrc = practitioner?.imageLocation;

  const enablePatientView = handlePatientReviewsView(
    enableCost,
    practitioner,
    displayVirtualVisit
  );

  const displayFutureTerminationDate = checkTerminationDate(
    displayTerminationDateEnabled,
    networkEndDate
  );
  const displayInNetworkBadge = isProviderStartDateInFuture(networkStartDate);
  const shouldShowVirtualVisitAddress = displayVirtualVisitAddress(
    isVirtualCareQuickCareSuppressed,
    value,
    address
  );

  const specialityTitleText = getSpecialityTitle(
    providerType,
    coverageType,
    providerSpeciality,
    t
  );
  const truncatedSpecialityTitle = truncateSpecialityTitle(
    isGroupedLocationDrawer,
    specialityTitleText,
    notXlScreen
  );
  const reciprocityId = getNetworkIdsForPES(
    currentMember,
    coverageType,
    featureFlags
  );

  const customPreferredPoliciesData: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();

  const customPreferredPoliciesWithLabel = customPreferredPoliciesData?.filter(
    (policy) => policy?.badgeLabel && policy?.icon
  );

  const selectedMember = getLoggedInMember();
  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    customPreferredPoliciesWithLabel,
    currentMember?.policyNumber,
    reciprocityId
  );

  const allConfigs = useLagoon('config')();
  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;
  const addToCompareText = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ADD_TO_COMPARE_TEXT,
  })?.message;
  const compareNowLinkText = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_LINK_COMPARE_NOW,
  })?.message;
  const bccBadgeTitle = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.BCC_PROVIDER,
  })?.title;

  const bccBadgeIcon = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.BCC_PROVIDER,
  })?.icon;

  const autoIncrementRadius = allConfigs?.find(
    (row) => row.key === 'AUTO_INCREMENT_SEARCH_RADIUS'
  )?.value;
  const isNewMapViewDataCardDesign =
    !expandedProviderListView && enableMapViewHideOption;
  const autoIncrementRadiusArr = getAutoIncrementRadius(autoIncrementRadius);

  const providerTierDesignation =
    practitioner?.recommendationDetails?.benefits?.providerTierDesignation;
  const llmStatus = practitioner?.recommendationDetails?.quality?.llmStatus;
  const updatedSearchRadius = searchFilters?.VirtualCare?.value
    ? autoIncrementRadiusArr?.[autoIncrementRadiusArr?.length - 1]
    : searchRadius;
  const suppressAddressAndMiles = shouldSuppressAddressAndMiles(
    enableVirtualCareEpic,
    isVirtualCareUnetRailroadSuppressed,
    virtualIndicator,
    value,
    eligibilities[planToken],
    allConfigs
  );

  const providerDetailsPath = getProviderDetailsRouteUrl(
    chipStore,
    providerId,
    locationId,
    showDirection,
    isTieredProvider,
    sectionTypeDerived,
    choosePCP,
    isTieredPlan,
    acoProvider,
    practitioner?.llmCodes,
    updatedSearchRadius,
    isNameSearch || isNotSpecialitySearch,
    providerTierDesignation,
    llmStatus
  );

  const providerDetailsUrl = getProviderDetailsUrl(
    portalData,
    providerDetailsPath
  );

  const isAcoBenefitPlan = getAcoBenefitPlan(
    defaultStringToEmpty(
      getProductCodeForCoverageType(
        selectedMember,
        ReverseCoverageTypesCodes.MEDICAL
      )
    ),
    defaultStringToEmpty(getMarketType(selectedMember)),
    defaultStringToEmpty(selectedMember?.membershipCategory),
    defaultStringToEmpty(selectedMember?.population),
    acoBenefitPlanCollection
  );

  const coverageTypeDental = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );

  const isDentalCare = ReverseDentalCodes.includes(
    coverageTypeDental.coverageType
  );

  const isSmartChoicePractitioner = useSmartChoice();
  const smartChoicePriceCheck = getIsSmartChoicePriceCheck(
    isSmartChoicePractitioner,
    isTieredPlan,
    isAcoBenefitPlan,
    acoProvider,
    isCustomPreferredPolicy
  );

  const {
    isTier2Provider,
    isPremiumCarePhysician,
    isPreferredLab = false,
    isFreeStandingFacility = false,
    isOxPreferredLab,
    recommendationDetails,
  } = practitioner || {};

  const isTier1ProviderEligible = displayTier1Identifier(isTieredProvider);

  const isDisplayTier1Identifier =
    showTierOneFlag &&
    getTierProviderEligibility(currentMember, isTier1ProviderEligible);

  const isDisplayTier2Identifier = displayTier2Identifier(
    isTier2Provider,
    currentMember?.isIFPTieredPlan
  );

  const customPreferredPolicies = customPreferredPoliciesData?.map(
    (policy) => ({ ...policy, policyId: removeLeadingZeroes(policy.policyId) })
  );

  const preferredIcon = providerPreferredIcon(
    currentMember,
    customPreferredPolicies,
    reciprocityId
  );

  const memberHasRPK = useStore(StoreKeys.TIER1_PLAN_FLAG);

  const suppressFacilityFlags = getSuppressFacilityFlags();

  const ddpCodeUser = getLoggedInMember()?.ddpCode;
  const ddpCode = practitioner?.ddpCode as FacilityDDPCode;
  const { isOXMember } = getIsOxfordAndUSP(
    currentMember,
    coverageType,
    featureFlags
  );

  const showBenefitsBadge = isShowPreferredFacilityBadge(
    suppressFacilityFlags,
    preferredFacilityFlag,
    ddpCode,
    ddpCodeUser,
    isPreferredLab,
    isOxPreferredLab,
    isOXMember,
    isFreeStandingFacility,
    oxfordPlnFlag
  );
  const showPremiumCare = displayPremiumCareIdentifier(
    !suppressPremiumCare,
    getPremiumCarePhysician(isPremiumCarePhysician),
    isDisplayTier1Identifier,
    isDisplayTier2Identifier,
    memberHasRPK,
    isCustomPreferredPolicy,
    isMultipleBadgeCardViewEnabled
  );

  const primarySecondaryContainerStyle = () => {
    let css;
    if (popupContainer) {
      css = popupSecondarySectionStyle;
    } else {
      css = SecondarySectionStyle;
    }

    if (showInternalCardInfo && badgeCardType === 'data-card') {
      css = {
        ...css,
        minHeight: 40,
      };
    }
    return css;
  };

  const planList = isNetworkInfo
    ? getPlanIndicator(planInfo, networkId, coverageType)
    : [];
  const carddInfoStyles = () => {
    let justifyContent = 'center';
    let margin = '0px';
    if (badgeCardType === 'data-card') {
      justifyContent = 'flex-start';
      margin = '4px 0px';
    }

    return {
      justifyContent,
      margin,
    };
  };

  const getHighlightClassName = () => {
    if (!expandedProviderListView) return highlightClassName;
    return '';
  };

  const getFlexDirection = () => {
    if (expandedProviderListView) return 'column';
    return 'row';
  };

  const getNetworkStatusIcon = () => {
    if (networkStatus) return '$success1';
    return '';
  };

  const isGEHAV2 = shouldUseGehaV2Styling();
  const additionalCssGEHA = isGEHAV2 ? { color: '#00172F' } : {};

  const networkDisplay = () => {
    if (popupContainer) {
      return <></>;
    }
    return (
      <NetworkDisplay
        color={getNetworkStatusIcon()}
        enableMapViewHideOption={enableMapViewHideOption}
        expandedProviderListView={expandedProviderListView ?? false}
        isGEHAV2={isGEHAV2}
        isGroupedLocationDrawer={isGroupedLocationDrawer}
        isNetworkInfo={isNetworkInfo}
        isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
        networkStatus={t(inOrOutOfNetwork(networkStatus))}
        networkStatusWithPlanName={`${
          t(inOrOutOfNetwork(networkStatus)) + ':'
        }`}
        planList={planList}
        providerId={providerId}
        sectionTypeDerived={sectionTypeDerived}
      />
    );
  };

  const avatarDisplay = () => (
    <ImageFlexContainer
      cssProps={{
        popupContainer,
        expandedProviderListView,
      }}
    >
      <DataCardAvatarContainer
        css={popupContainer ? { paddingBottom: '10px' } : {}}
      >
        {compareShareToggle && (
          <Checkbox
            css={{
              'abyss-checkbox-root': { marginTop: '4px', marginBottom: '8px' },
            }}
            data-auto-testid="map-view-checkbox-group"
            data-testid="map-view-checkbox-group"
            isChecked={
              selectedCheckbox?.checked[practitioner?.locationId as string] ||
              false
            }
            isDisabled={isCheckboxDisabled(practitioner, selectedCheckbox)}
            key={practitioner?.locationId}
            label={
              <Flex>
                {Object.values(selectedCheckbox?.checked)?.filter(
                  (item) => item
                )?.length >= 2 &&
                Object.keys(selectedCheckbox.checked)
                  ?.filter((item) => selectedCheckbox?.checked[item])
                  .includes(practitioner?.locationId as string) ? (
                  <Link
                    css={{
                      '&:hover::before': { display: 'none' },
                      marginLeft: '2px',
                      fontSize: '14px',
                    }}
                    data-testid="compare-now-button"
                    onClick={setIsCompareClicked}
                  >
                    {compareNowLinkText?.replace(
                      '{{X}}',
                      Object.values(selectedCheckbox?.checked)?.filter(
                        (item) => item
                      )?.length
                    )}
                  </Link>
                ) : (
                  <Text css={{ fontSize: 14 }}>{addToCompareText}</Text>
                )}
              </Flex>
            }
            onChange={() => {
              if (!isCheckboxDisabled(practitioner, selectedCheckbox)) {
                setSelectedCheckbox((prevState) => ({
                  checked: {
                    ...prevState.checked,
                    [practitioner?.locationId as string]:
                      !prevState.checked[practitioner?.locationId as string],
                  },
                }));
                addSelectedProvider(practitioner);
                adobeLinkTrackEventCheckBox(
                  selectedCheckbox?.checked[practitioner?.locationId as string],
                  index
                );
                setLastSelectedIndex(index);
              }
            }}
            size={18}
          />
        )}
        <AvatarDataCard
          cssProps={{
            expandedProviderListView,
            isGroupedLocationDrawer,
            enableMapViewHideOption,
          }}
        >
          {ImageComponent({
            imgRef,
            providerType,
            imgSrc,
            bhIcon,
            sizeToken: enableMapViewHideOption
              ? '$snackCardIconSize'
              : '$dataCardIconSize',
            expandedProviderListView,
            isGroupedLocationDrawer,
            notXlScreen,
            TabletScreen,
            enableMapViewHideOption,
          })}
        </AvatarDataCard>
        {displayVirtualVisit && !popupContainer && !enableMapViewHideOption && (
          <Flex
            color="$primary1"
            css={displayVirtualVisitStyle(isNewMapViewDataCardDesign)}
            data-auto-testid="videocam-icon"
          >
            <IconMaterial
              aria-hidden="true"
              color="white"
              icon="videocam"
              size={16}
              variant={isGEHAV2 ? 'outlined' : 'filled'}
            />
          </Flex>
        )}

        {enableMapViewHideOption && (
          <>
            <Flex
              css={SmartChoiceListViewAvatar(
                expandedProviderListView,
                isGroupedLocationDrawer,
                isSmartChoicePractitioner
              )}
              data-testid={`data-card-network-${sectionTypeDerived}-section`}
              direction="row"
              justify="center"
            >
              {isSmartChoicePractitioner &&
                enableMapViewHideOption &&
                providerType?.toLowerCase() ===
                  ProviderType.PRACTITIONER.toLowerCase() &&
                parseInt(
                  recommendationDetails?.totalRecommendationScore || '0'
                ) > 0 && (
                  <SmartChoiceScoreCard
                    expandedProviderListView={expandedProviderListView}
                    isDataCard
                    isGroupedLocationDrawer={isGroupedLocationDrawer}
                    recommendationDetails={
                      recommendationDetails as RecommendationDetails
                    }
                  />
                )}
            </Flex>
            <Flex
              css={networkDisplayStyles(
                expandedProviderListView,
                enableMapViewHideOption,
                isSmartChoicePractitioner
              )}
            >
              {networkDisplay()}
            </Flex>
          </>
        )}
      </DataCardAvatarContainer>
    </ImageFlexContainer>
  );

  const nameAndBookmarkDisplay = () => (
    <Flex
      css={
        isGroupedLocationDrawer
          ? {}
          : cardBookmarkButtonStyles(isNewMapViewDataCardDesign, popupContainer)
      }
    >
      <NameHeading
        data-auto-testid="data-card-heading-container"
        data-testid="data-card-heading-container"
        enableMapViewHideOption={enableMapViewHideOption}
        expandedProviderListView={expandedProviderListView}
        handleDetailsOnClick={handleDetailsOnClick}
        handleHoverOnCard={handleHoverOnCard}
        handleOnHeadingBlur={handleOnHeadingBlur}
        href={providerDetailsUrl}
        isGroupedLocationDrawer={isGroupedLocationDrawer}
        isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
        name={name}
        popupContainer={popupContainer}
        providerId={providerId}
        sectionTypeDerived={sectionTypeDerived}
        shouldWrapName={shouldWrapName}
      />
      {enableCareTeamView &&
        !popupContainer &&
        !isGroupedLocationDrawer &&
        !expandedProviderListView && (
          <BookmarkButton
            expandedProviderListView={expandedProviderListView}
            fromDataCard={true}
            locationId={locationId}
            providerId={providerId}
            providerName={name}
            providerType={providerType}
          />
        )}
    </Flex>
  );

  const dentalAndSpecialityDisplay = () => (
    <>
      {isDentalLocationNameEnable && isDentalCare && (
        <Tooltip content={practitioner?.address?.locationName}>
          <SnackRole
            css={{ fontWeight: 'bold' }}
            cssProps={{ isGEHAV2 }}
            data-auto-testid="get-provider-location-name"
            data-testid="get-provider-location-name"
          >
            {practitioner?.address?.locationName}
          </SnackRole>
        </Tooltip>
      )}
      <Flex css={flexSpecialityAndBadgeStyle(badgeCardType)}>
        {' '}
        {/* Added styles here */}
        <Flex>
          {
            <Tooltip
              content={specialityTitleText}
              css={
                popupContainer
                  ? popupSpecialityNameToolTipStyles
                  : expandedProviderListView
                  ? providerNameListViewToolTipStyles
                  : specialityNameToolTipStyles(enableMapViewHideOption)
              }
              position="top"
              positionOffset={8}
            >
              <ResponsiveTextSpeciality
                as="div"
                css={
                  isGroupedLocationDrawer && {
                    fontSize: notXlScreen ? '16px' : '18px',
                    color: '$gray8',
                  }
                }
                cssProps={{
                  expandedProviderListView,
                  enableMapViewHideOption,
                  isGEHAV2,
                }}
                data-auto-testid={`data-card-specialty-${sectionTypeDerived}`}
                data-testid={`data-card-specialty-${sectionTypeDerived}`}
              >
                {truncatedSpecialityTitle}
              </ResponsiveTextSpeciality>
            </Tooltip>
          }
        </Flex>
        {!enableMapViewHideOption && <Flex>{networkDisplay()}</Flex>}
      </Flex>
    </>
  );

  const virtualVisitDisplay = () => {
    if (expandedProviderListView) {
      return (
        <>
          {displayVirtualVisit && !popupContainer && !suppressVirtualCare && (
            <Layout.Group
              css={
                isGroupedLocationDrawer
                  ? VirtualVisitPatientStyleGroupedLocation
                  : enablePatientView
                  ? VirtualVisitPatientStyle(enableMapViewHideOption)
                  : VirtualVisitStyle(enableMapViewHideOption)
              }
            >
              <IconMaterial
                color="$gray8"
                data-auto-testid="videocam-icon"
                icon="videocam"
                size={isGroupedLocationDrawer && notXlScreen ? 16 : 20}
                variant="outlined"
              />
              <Tooltip
                content={t('DETAILS_PAGE.OFFERS_VIRTUAL_CARE') || ''}
                css={virtualVistsToolTipStyles(
                  enableMapViewHideOption,
                  expandedProviderListView
                )}
                position="top"
                positionOffset={8}
              >
                <VirtualVisitLabel
                  as="div"
                  css={{
                    ...(isGroupedLocationDrawer && {
                      color: '$gray8',
                      fontSize: notXlScreen ? '14px' : '16px',
                    }),
                    ...(expandedProviderListView &&
                      !isGroupedLocationDrawer && {
                        color: isGEHAV2 ? '$primary1' : '$gray8',
                        fontSize: TabletScreen ? '16px' : '18px',
                      }),
                  }}
                  data-auto-testid={`data-card-virtual-visits-${sectionTypeDerived}`}
                  data-testid={`data-card-virtual-visits-${sectionTypeDerived}`}
                  fontWeight="$medium"
                >
                  {t('DETAILS_PAGE.OFFERS_VIRTUAL_CARE')}
                </VirtualVisitLabel>
              </Tooltip>
            </Layout.Group>
          )}
        </>
      );
    } else {
      return (
        <>
          {displayVirtualVisit && !suppressVirtualCare && !popupContainer && (
            <Layout.Group
              css={
                enablePatientView
                  ? VirtualVisitPatientStyle(enableMapViewHideOption)
                  : VirtualVisitStyle(enableMapViewHideOption)
              }
            >
              <IconMaterial
                color="$virtualVisitIconColor"
                data-auto-testid="videocam-icon"
                icon="videocam"
                size={20}
                variant={isGEHAV2 ? 'outlined' : 'filled'}
              />
              <Tooltip
                content={t('DETAILS_PAGE.OFFERS_VIRTUAL_CARE') || ''}
                css={virtualVistsToolTipStyles(
                  enableMapViewHideOption,
                  expandedProviderListView ?? false,
                  isGEHAV2
                )}
                position="top"
                positionOffset={8}
              >
                <VirtualVisitLabel
                  as="div"
                  data-auto-testid={`data-card-virtual-visits-${sectionTypeDerived}`}
                  data-testid={`data-card-virtual-visits-${sectionTypeDerived}`}
                  fontWeight="$medium"
                >
                  {t('DETAILS_PAGE.OFFERS_VIRTUAL_CARE')}
                </VirtualVisitLabel>
              </Tooltip>
            </Layout.Group>
          )}
        </>
      );
    }
  };
  const ratingsDisplay = (patientRatingStarsRef) => (
    <>
      {!suppressPatientReviews && (
        <Flex
          alignItems="center"
          css={{
            'abyss-flex-root': {
              cursor: shouldShowCursorOnRatings(healthGradeRating),
              height: '18px',
              ...additionalRatingStyle(expandedProviderListView),
            },
          }}
          data-testid="data-card-clickable-reviews"
          onClick={ratingsOnClickHandler(
            healthGradeRating,
            handleDetailsOnClick
          )}
        >
          {sectionTypeDerived === Constants.RESULT_SECTION.PROVIDER && (
            <ProviderRating
              data-testid={`data-card-rating-${sectionTypeDerived}`}
              enableMapViewHideOption={enableMapViewHideOption}
              expandedProviderListView={expandedProviderListView}
              healthGradeRating={healthGradeRating}
              isGroupedLocationDrawer={isGroupedLocationDrawer}
              papatientRatingStarsRef={patientRatingStarsRef}
              popupContainer={popupContainer}
              providerId={providerId}
              sectionTypeDerived={sectionTypeDerived}
              size={valCheck(mobileScreen, 12, 13)}
              surveyCount={practitioner?.surveyCount}
              underline={false}
            />
          )}
        </Flex>
      )}
    </>
  );

  const milesAndAddressDisplay = () => (
    <div ref={addressMilesRef}>
      <Flex
        alignItems={
          isGroupedLocationDrawer
            ? 'flex-start'
            : expandedProviderListView
            ? 'flex-end'
            : 'center'
        }
        css={milesAndAddressStyles(
          expandedProviderListView,
          isGroupedLocationDrawer
        )}
      >
        {!suppressAddressAndMiles && (
          <>
            <>
              <Button
                aria-labelledby={`LocName${providerId} LocAdrs${providerId}`}
                css={
                  isGroupedLocationDrawer
                    ? CardButtonGroupedLocationStyle
                    : CardButtonStyle
                }
                data-auto-testid="datacard-address"
                data-testid="datacard-address"
                id="miles-button"
                onClick={() => {
                  handleAdobeSearchResultClickEvent('address');
                  handleNavigationOnClick();
                }}
                role="button"
                variant="tertiary"
              >
                {!popupContainer && (
                  <Address
                    address={address}
                    badgeCardType={badgeCardType}
                    enableMapViewHideOption={enableMapViewHideOption}
                    expandedProviderListView={expandedProviderListView}
                    id={providerId}
                    isCTADisplayLogic={ctaDisplayLogic}
                    isGroupedLocationDrawer={isGroupedLocationDrawer}
                    sectionType={sectionTypeDerived}
                    showSmartChoice={showSmartChoice}
                  />
                )}
              </Button>

              <VoiceOnlyText>
                {t('DIRECTIONS_FOR')} {name}
              </VoiceOnlyText>
            </>
            <div
              aria-labelledby={`LocMiles${providerId}`}
              data-auto-testid={`data-card-location-address-${address}-${providerId}`}
              data-testid={`data-card-location-address-${address}-${providerId}`}
              style={{
                height: expandedProviderListView
                  ? '40px'
                  : mobileScreen
                  ? '20px'
                  : '50px',
              }}
            >
              <Miles
                addressMilesRef={isGroupedLocationDrawer && addressMilesRef}
                distance={distance || ''}
                enableMapViewHideOption={enableMapViewHideOption}
                expandedProviderListView={expandedProviderListView}
                headers={headers}
                id={providerId}
                indexForAnalytics={getLinkPositionIndex(index)}
                isCTADisplayLogic={ctaDisplayLogic}
                isGroupedLocationDrawer={isGroupedLocationDrawer}
                isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
                locationForAnalytics={linkLocationName}
                onClosePopup={() => setIsOpen(false)}
                onMilesClick={onCardClick}
                popupContainer={popupContainer}
                practitioner={practitioner}
                providerType={providerType}
                searchTerm={searchTerm}
                sectionType={sectionTypeDerived}
                selectedFilters={selectedFilters}
                showBestMatchDetails={showBestMatchDetails}
                showSmartChoice={showSmartChoice}
              />
            </div>
          </>
        )}
        {showSmartChoice && !isGroupedLocationDrawer && (
          <Button
            css={CardButtonViewListStyle(expandedProviderListView)}
            data-auto-testid="datacard-smart-choice"
            data-testid="datacard-smart-choice"
            onClick={() => {
              handleSmartChoiceClick && handleSmartChoiceClick();
            }}
            style={{
              position: 'relative',
              top: expandedProviderListView ? '-13px' : '-7px',
            }}
            variant="ghost"
          >
            <Text
              css={SmartChoiceFontSizeStyle(
                expandedProviderListView,
                TabletScreen
              )}
            >
              {'SC'}
            </Text>
          </Button>
        )}
      </Flex>
    </div>
  );

  const groupedLocationBadgeCss = (isGroupedLocationDrawer ||
    enableMapViewHideOption) && {
    fontSize: '16px',
    fontWeight: '600',
    '@media (max-width: 1248px)': {
      fontSize: '14px',
    },
  };

  const groupedLocationBadgeIconSize = (defaultSize) =>
    isGroupedLocationDrawer ? (notXlScreen ? 16 : 20) : defaultSize;

  const badgeDisplayForUMR = (isUmr: boolean, topOrMiddle: string) => (
    <>
      {valCheck(
        isCustomPreferredPolicy &&
          isGreenDiamondIndicator &&
          isUmr &&
          topOrMiddle === 'top',
        <div ref={preferredProviderRef}>
          <Flex alignItems="center" direction="row">
            <PSXIcon
              icon={preferredIcon}
              size={groupedLocationBadgeIconSize(badgeSize)}
            />
            <Text
              css={{
                ...groupedLocationBadgeCss,
                marginLeft: '4px',
                ...ProviderMetaFontSizeStyle(
                  expandedProviderListView,
                  TabletScreen
                ),
              }}
            >
              {providerPreferredHeading(
                selectedMember ?? currentMember,
                customPreferredPolicies,
                reciprocityId
              )}
            </Text>
          </Flex>
        </div>,
        null
      )}
      {
        // TIER 1
        valCheck(
          !isCustomPreferredPolicy &&
            !isGreenDiamondIndicator &&
            isDisplayTier1Identifier &&
            topOrMiddle === 'top',
          <div ref={tier1BadgeRef}>
            <Flex
              alignItems="center"
              data-testid="tier-1-badge"
              direction="row"
            >
              <PSXIcon
                icon="Group 204976975"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />
              <Text
                css={{
                  ...groupedLocationBadgeCss,
                  marginLeft: '4px',
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                }}
              >
                {t('Tier 1')}
              </Text>
            </Flex>
          </div>,
          null
        )
      }
      {
        // TIER 2
        valCheck(
          !isCustomPreferredPolicy &&
            !isGreenDiamondIndicator &&
            !isDisplayTier1Identifier &&
            isDisplayTier2Identifier &&
            isUmr &&
            topOrMiddle === 'top',
          <div ref={tier2BadgeRef}>
            <Flex
              alignItems="center"
              data-testid="tier-2-badge"
              direction="row"
            >
              <PSXIcon
                icon="tier_two"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />{' '}
              <Text
                css={{
                  ...groupedLocationBadgeCss,
                  marginLeft: '4px',
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                }}
              >
                {t('Tier 2')}
              </Text>
            </Flex>
          </div>,
          null
        )
      }
      {valCheck(
        isPlatinumProviderIdentifierEnabled &&
          isPlatinumProvider &&
          isUmr &&
          topOrMiddle === 'middle',
        <div ref={platinumBadgeRef}>
          <Flex
            alignItems="center"
            data-testid="platinum-recognition-badge-container"
            direction="row"
          >
            <Text
              css={{
                color: '#323333',
                fontWeight: '600',
                ...groupedLocationBadgeCss,
                marginLeft: '4px',
                ...ProviderMetaFontSizeStyle(
                  expandedProviderListView,
                  TabletScreen
                ),
              }}
            >
              {t('PROVIDER_DETAILS.ABOUT_TAB.PLATINUM_PROVIDER')}
            </Text>
          </Flex>
        </div>,
        null
      )}
      {
        // PREMIUM CARE BADGE
        valCheck(
          isPremiumCareDisplayEligible(
            !suppressPremiumCare,
            getPremiumCarePhysician(isPremiumCarePhysician)
          ) &&
            !suppressPremiumCare &&
            !currentMember?.isCnSMember &&
            isUmr &&
            topOrMiddle === 'middle',
          <div ref={premiumCareBadgeRef}>
            <Flex
              alignItems="center"
              css={{ minWidth: 'max-content' }}
              direction="row"
            >
              <IconSymbol
                color="$primary1"
                icon="favorite"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />
              <IconSymbol
                color="$primary1"
                icon="favorite"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />
              <Text
                css={{
                  ...groupedLocationBadgeCss,
                  marginLeft: '4px',
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                  ...additionalCssGEHA,
                }}
              >
                {t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_PHYSICIAN_TAG')}
              </Text>
            </Flex>
          </div>,
          null
        )
      }
      {valCheck(
        !isGreenDiamondIndicator &&
          !!displayPreferredFacilityOnCards(
            showPremiumCare,
            showBenefitsBadge,
            isDisplayTier1Identifier,
            isDisplayTier2Identifier
          ) &&
          isUmr &&
          topOrMiddle === 'middle',
        <div ref={preferredFacilityRef}>
          <FacilityTagStyle
            alignItems="flex-end"
            cssProps={{ isAncillaryCard: false, expandedProviderListView }}
            direction="column"
          >
            <Flex alignItems="center" direction="row">
              <FacilityTextStyle
                cssProps={{
                  expandedProviderListView,
                  enableMapViewHideOption,
                  isGEHAV2,
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                }}
              >
                {t('PREFERRED_FACILITY_BENEFITS.TAG')}
              </FacilityTextStyle>
            </Flex>
          </FacilityTagStyle>
        </div>,
        null
      )}
    </>
  );

  const showBccEligible = getBccBadgeState(
    practitioner?.isBccProvider,
    portalData,
    enableBccBadge
  );

  const badgesDisplay = () => (
    <>
      {valCheck(
        !showBccEligible &&
          isPlatinumProviderIdentifierEnabled &&
          isPlatinumProvider,
        <div ref={platinumBadgeRef}>
          <Flex
            alignItems="center"
            data-testid="platinum-recognition-badge-container"
            direction="row"
          >
            <Text
              css={{
                color: '#323333',
                fontWeight: '600',
                ...groupedLocationBadgeCss,
                marginLeft: '4px',
                ...ProviderMetaFontSizeStyle(
                  expandedProviderListView,
                  TabletScreen
                ),
              }}
            >
              {t('PROVIDER_DETAILS.ABOUT_TAB.PLATINUM_PROVIDER')}
            </Text>
          </Flex>
        </div>,
        null
      )}

      {
        // TIER 1
        valCheck(
          !showBccEligible && isDisplayTier1Identifier,
          <div ref={tier1BadgeRef}>
            <Flex
              alignItems="center"
              data-testid="tier-1-badge"
              direction="row"
            >
              <PSXIcon
                icon="Group 204976975"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />
              <Text
                css={{
                  ...groupedLocationBadgeCss,
                  marginLeft: '4px',
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                }}
              >
                {t('Tier 1')}
              </Text>
            </Flex>
          </div>,
          null
        )
      }

      {
        // PREMIUM CARE BADGE
        valCheck(
          isPremiumCareDisplayEligible(
            !suppressPremiumCare,
            getPremiumCarePhysician(isPremiumCarePhysician)
          ) &&
            !showBccEligible &&
            showPremiumCare &&
            !currentMember?.isCnSMember,
          <div ref={premiumCareBadgeRef}>
            <Flex
              alignItems="center"
              css={{ minWidth: 'max-content' }}
              direction="row"
            >
              <IconSymbol
                color="$primary1"
                icon="favorite"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />
              <IconSymbol
                color="$primary1"
                icon="favorite"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />
              <Text
                css={{
                  ...groupedLocationBadgeCss,
                  marginLeft: '4px',
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                  ...additionalCssGEHA,
                }}
              >
                {t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_PHYSICIAN_TAG')}
              </Text>
            </Flex>
          </div>,
          null
        )
      }

      {
        // TIER 2
        valCheck(
          !showBccEligible && isDisplayTier2Identifier,
          <div ref={tier2BadgeRef}>
            <Flex
              alignItems="center"
              data-testid="tier-2-badge"
              direction="row"
            >
              <PSXIcon
                icon="tier_two"
                size={groupedLocationBadgeIconSize(badgeSize)}
              />{' '}
              <Text
                css={{
                  ...groupedLocationBadgeCss,
                  marginLeft: '4px',
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                }}
              >
                {t('Tier 2')}
              </Text>
            </Flex>
          </div>,
          null
        )
      }

      {valCheck(
        showBccEligible,
        <div
          data-testid="priority-scheduling-provider-container"
          ref={preferredProviderRef}
        >
          <Flex alignItems="center" direction="row">
            <PSXIcon
              data-testid="priority-scheduling-provider-icon"
              icon={bccBadgeIcon}
              size={groupedLocationBadgeIconSize(badgeSize)}
            />
            <Text
              css={{
                ...groupedLocationBadgeCss,
                marginLeft: '4px',
                ...ProviderMetaFontSizeStyle(
                  expandedProviderListView,
                  TabletScreen
                ),
              }}
              data-testid="priority-scheduling-provider-text"
            >
              {bccBadgeTitle}
            </Text>
          </Flex>
        </div>,
        null
      )}
      {
        // PREFERRED PROVIDER / - GREEN DIAMOND
        valCheck(
          !showBccEligible &&
            isCustomPreferredPolicy &&
            isGreenDiamondIndicator,
          <div ref={preferredProviderRef}>
            <Flex alignItems="center" direction="row">
              <PSXIcon
                icon={preferredIcon}
                size={groupedLocationBadgeIconSize(badgeSize)}
              />
              <Text
                css={{
                  ...groupedLocationBadgeCss,
                  marginLeft: '4px',
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                }}
              >
                {providerPreferredHeading(
                  selectedMember ?? currentMember,
                  customPreferredPolicies,
                  reciprocityId
                )}
              </Text>
            </Flex>
          </div>,
          null
        )
      }

      {valCheck(
        !showBccEligible &&
          !isGreenDiamondIndicator &&
          !!displayPreferredFacilityOnCards(
            showPremiumCare,
            showBenefitsBadge,
            isDisplayTier1Identifier,
            isDisplayTier2Identifier
          ),
        <div ref={preferredFacilityRef}>
          <FacilityTagStyle
            alignItems="flex-end"
            cssProps={{ isAncillaryCard: false, expandedProviderListView }}
            direction="column"
          >
            <Flex alignItems="center" direction="row">
              <FacilityTextStyle
                cssProps={{
                  expandedProviderListView,
                  enableMapViewHideOption,
                  isGEHAV2,
                  ...ProviderMetaFontSizeStyle(
                    expandedProviderListView,
                    TabletScreen
                  ),
                }}
              >
                {t('PREFERRED_FACILITY_BENEFITS.TAG')}
              </FacilityTextStyle>
            </Flex>
          </FacilityTagStyle>
        </div>,
        null
      )}
    </>
  );

  const costDisplay = () => (
    <>
      {/* COST INDICATOR */}
      {enableCost &&
        portalShowCost &&
        !popupContainer &&
        practitioner?.costIndicator &&
        !suppressCostEstimates &&
        smartChoicePriceCheck && (
          <div ref={costIndicatorRef}>
            <Flex
              css={costIndicatorStyles(
                enableMapViewHideOption,
                expandedProviderListView,
                TabletScreen
              )}
            >
              <CostIndicatorPopover
                callFrom="datacard"
                costIndicator={practitioner.costIndicator}
                enableMapViewHideOption={enableMapViewHideOption}
                headers={headers}
                indexForAnalytics={getLinkPositionIndex(index)}
                isGroupedLocationDrawer={isGroupedLocationDrawer}
                isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
                locationForAnalytics={linkLocationName}
                practitioner={practitioner}
                providerType={providerType}
                searchTerm={searchTerm}
                selectedFilters={selectedFilters}
              />
            </Flex>
          </div>
        )}
    </>
  );

  const callAndScheduleDisplay = () => (
    <>
      {!popupContainer && (
        <ChoosePCP
          choosePCP={choosePCP}
          doShowDocASAP={doShowDocASAP}
          docasapPracticeId={docasapPracticeId}
          enableMapViewHideOption={enableMapViewHideOption}
          expandedProviderListView={expandedProviderListView}
          getLinkPositionIndex={getLinkPositionIndex}
          handleAdobeSearchResultClickEvent={handleAdobeSearchResultClickEvent}
          handleChoosePCPClick={handleChoosePCPClick}
          handleNavigationOnClick={handleNavigationOnClick}
          headers={headers}
          index={index}
          isCTADisplayLogic={ctaDisplayLogic}
          isGroupedLocationDrawer={isGroupedLocationDrawer}
          isLimitedAccessToProvider={
            isLimitedAccessToProvider &&
            practitioner?.hospitalBasedProviderIndicator
          }
          isSchedulingDataIndicator={
            ascesionIndicatorEnabled &&
            checkAscensionScheduleDataIndicator(practitioner, coverageType)
          }
          isSchedulingWithFacilityEnabled={
            isSchedulingWithFacilityEnabled &&
            coverageType === ReverseCoverageTypesCodes['MEDICAL']
          }
          isTieredDataCard={isTieredProvider}
          isUmr={isUmr}
          linkLocationName={linkLocationName}
          locationId={locationId}
          name={name}
          npi={npi}
          practitioner={practitioner}
          primaryPhone={primaryPhone}
          providerId={providerId}
          providerType={providerType}
          searchTerm={searchTerm}
          sectionTypeDerived={sectionTypeDerived}
          selectedFilters={selectedFilters}
          showDirection={showDirection}
        />
      )}
    </>
  );
  if (
    (isNewMapViewDataCardDesign && !popupContainer) ||
    (isGroupedLocationDrawer && smallScreen)
  ) {
    return (
      <CardContainer
        className={getHighlightClassName()}
        css={{
          ...cardContainerCss,
          ...cardContainerHeightCss(
            isNetworkAlertIndicator,
            isInNetworkBadge,
            displayInNetworkBadgeEnabled,
            isCurrentMemberPreEffective
          ),
          ...cardContainerBorderCss(enableMapEnhancements, isPopup),
          ...cardHeightForSmartChoice(
            showInternalCardInfo,
            enableMapViewHideOption,
            isUmr
          ),
          ...renderWidth(isPlatinumProvider, popupContainer),
        }}
        cssProps={{
          enableMapViewHideOption,
          expandedProviderListView,
          isGroupedLocationDrawer,
        }}
        data-auto-testid="generic-data-card"
        data-testid="generic-data-card"
        id={`data-card-for-map-${providerId}`}
        onMouseEnter={conditionalHandler(
          expandedProviderListView,
          handleHoverOnCard
        )}
        onMouseLeave={conditionalHandler(
          expandedProviderListView,
          handleExitOnCard
        )}
        ref={cardRef}
      >
        <ResultCard>
          <Card.Section
            css={{
              gridArea: 'name',
              ...cardSectionStyle(isGroupedLocationDrawer, smallScreen),
            }}
          >
            <Flex direction="column">
              {isUmr && badgeDisplayForUMR(isUmr, 'top')}
              <Flex>{nameAndBookmarkDisplay()}</Flex>
              <Flex>{dentalAndSpecialityDisplay()}</Flex>
            </Flex>
          </Card.Section>
          <Card.Section
            css={{
              gridArea: 'avatar',
              borderTop: 'none !important',
              ...(popupContainer
                ? PopupContainerStyle
                : PrimarySectionStyle(isNewMapViewDataCardDesign)),
            }}
            data-testid="data-card-details-avatar-section"
          >
            {avatarDisplay()}
          </Card.Section>
          <Card.Section
            css={{
              gridArea: 'content',
              ...cardSectionStyle(isGroupedLocationDrawer, smallScreen),
            }}
          >
            <Flex
              css={{ gap: '8px' }}
              data-testid="data-card-details-info-section"
              direction="column"
            >
              <Flex css={{ gap: '4px' }} direction="column">
                {isUmr ? badgeDisplayForUMR(isUmr, 'middle') : badgesDisplay()}
                {ratingsDisplay(patientRatingStarsRef)}
                {costDisplay()}
                {virtualVisitDisplay()}
              </Flex>
              <Flex>{milesAndAddressDisplay()}</Flex>
            </Flex>
          </Card.Section>
        </ResultCard>
        <Card.Section
          css={{
            width: isGroupedLocationDrawer && smallScreen ? '325px' : '',
            padding: '0px 4px 0px 0px',
          }}
        >
          {callAndScheduleDisplay()}
        </Card.Section>
      </CardContainer>
    );
  }

  const getPaddingRight = (
    isGroupedLocationDrawer: boolean,
    expandedProviderListView: boolean
  ): string => {
    if (isGroupedLocationDrawer) {
      return '';
    }
    return expandedProviderListView ? '32px' : '48px';
  };

  return (
    <>
      {isSmartChoicePractitioner &&
        showInternalCardInfo &&
        badgeCardType === 'snack-card-map-popup' &&
        providerType?.toLowerCase() ===
          ProviderType.PRACTITIONER.toLowerCase() &&
        parseInt(recommendationDetails?.totalRecommendationScore || '0') >
          0 && (
          <SmartChoiceScoreCard
            expandedProviderListView={expandedProviderListView}
            isDataCard
            isGroupedLocationDrawer={isGroupedLocationDrawer}
            isSnackCardOnTheMap
            recommendationDetails={
              recommendationDetails as RecommendationDetails
            }
          />
        )}

      <CardContainer
        className={getHighlightClassName()}
        css={
          !expandedProviderListView
            ? {
                ...cardContainerCss,
                ...infoCardHeight(maxBadgeCount),
                ...cardContainerBorderCss(enableMapEnhancements, isPopup),
                ...cardHeightForSmartChoice(
                  showInternalCardInfo,
                  enableMapViewHideOption,
                  isUmr
                ),
                ...renderWidth(isPlatinumProvider, popupContainer),
              }
            : {}
        }
        cssProps={{
          expandedProviderListView,
          enableMapViewHideOption,
          isNewMapViewDataCardDesign,
        }}
        data-auto-testid="generic-data-card"
        data-testid="generic-data-card"
        id={`data-card-for-map-${providerId}`}
        onMouseEnter={conditionalHandler(
          expandedProviderListView,
          handleHoverOnCard
        )}
        onMouseLeave={conditionalHandler(
          expandedProviderListView,
          handleExitOnCard
        )}
        ref={cardRef}
      >
        {isUmr &&
          popupContainer &&
          isMultipleBadgeEnabled &&
          showInternalCardInfo && (
            <MapPopupPremiumCareCardInfo>
              <CardInfoForUMR
                cardType="snack-card-map-popup-internal"
                displayMiddle={false}
                displayTop={true}
                headers={headers}
                indexForAnalytics={getLinkPositionIndex(index)}
                isSmartChoiceFFEnabled={showInternalCardInfo}
                landingPage={false}
                locationForAnalytics={linkLocationName}
                onClosePopup={() => setIsOpen(false)}
                popupContainer={popupContainer}
                practitioner={practitioner}
                searchTerm={searchTerm}
                selectedFilters={selectedFilters}
              />
            </MapPopupPremiumCareCardInfo>
          )}

        {!isUmr &&
          popupContainer &&
          isMultipleBadgeEnabled &&
          !showInternalCardInfo && (
            <MapPopupPremiumCareCardInfo>
              <CardInfo
                cardType="snack-card-map-popup-internal"
                headers={headers}
                indexForAnalytics={getLinkPositionIndex(index)}
                landingPage={false}
                locationForAnalytics={linkLocationName}
                practitioner={practitioner}
              />
            </MapPopupPremiumCareCardInfo>
          )}
        <Card.Section
          css={getPrimarySectionStyle(
            popupContainer,
            isNewMapViewDataCardDesign
          )}
          data-testid="data-card-details-primary-section"
          onClick={conditionalHandler(
            expandedProviderListView,
            handleDetailsOnClick
          )}
          style={expandedProviderListView ? {} : { cursor: 'pointer' }}
        >
          <Flex justify={valCheck(popupContainer, 'center', '')}>
            {avatarDisplay()}
            <Flex
              css={nameAndBadgeStyle(
                popupContainer,
                expandedProviderListView,
                isGroupedLocationDrawer
              )}
              direction="column"
            >
              {displayFutureTerminationDate && !popupContainer && (
                <TerminationDateIndicatorContainer data-testid="termination-date-container">
                  <TerminationStatusIndicator
                    message={t(
                      'DISPLAY_FUTURE_TERMINATION_DATE.NETWORK_ALERT_DATACARD'
                    )}
                  />
                </TerminationDateIndicatorContainer>
              )}
              {displayInNetworkBadge &&
                !popupContainer &&
                displayInNetworkBadgeEnabled && (
                  <InNetworkBadgeContainer data-testid="in-network-badge-container">
                    <InNetworkBadgeWithDate
                      message={t(
                        'DISPLAY_IN_NETWORK_BADGE.IN_NETWORK_BADGE_PCP'
                      )}
                      startDate={networkStartDate}
                    />
                  </InNetworkBadgeContainer>
                )}
              {isPlatinumProviderIdentifierEnabled &&
                isPlatinumProvider &&
                !enableMapViewHideOption && (
                  <TerminationDateIndicatorContainer
                    aria-label="platinum recognition badge container"
                    data-testid="platinum-recognition-badge-container"
                  >
                    <Badge variant="neutral">
                      {t('PROVIDER_DETAILS.ABOUT_TAB.PLATINUM_PROVIDER')}
                    </Badge>
                  </TerminationDateIndicatorContainer>
                )}
              <Flex
                css={{
                  padding: '2px 0 2px $sm',
                  gap: expandedProviderListView ? '16px' : '2px',
                }}
              >
                {isUmr &&
                  !popupContainer &&
                  isMultipleBadgeEnabled &&
                  showInternalCardInfo &&
                  badgeDisplayForUMR(isUmr, 'top')}
              </Flex>
              {nameAndBookmarkDisplay()}
              <Flex
                css={{
                  padding: '2px 0 2px $sm',
                  gap: expandedProviderListView ? '16px' : '2px',
                }}
                direction={getFlexDirection()}
                justify={getFlexJustification(popupContainer)}
              >
                {dentalAndSpecialityDisplay()}
                {isGroupedLocationDrawer
                  ? !notXlScreen && virtualVisitDisplay()
                  : enableMapViewHideOption && virtualVisitDisplay()}
                {enableMapViewHideOption && !popupContainer && (
                  <>{milesAndAddressDisplay()}</>
                )}
              </Flex>
            </Flex>
            {expandedProviderListView && (
              <Flex
                alignItems="flex-end"
                css={{
                  display: 'flex',
                  width: '20%',
                  paddingRight: getPaddingRight(
                    isGroupedLocationDrawer,
                    expandedProviderListView
                  ),
                  gap: '8px',
                  flex: '1 0 0',
                  paddingTop: isGroupedLocationDrawer ? '' : '6px',
                }}
                direction="column"
              >
                {renderBookmarkButton(
                  tablet,
                  expandedProviderListView,
                  locationId,
                  providerId,
                  providerType,
                  enableCareTeamView,
                  name
                )}
                {isUmr &&
                  !isNewMapViewDataCardDesign &&
                  !popupContainer &&
                  badgeDisplayForUMR(isUmr, 'top')}
                {badgesDisplay()}

                {/* PATIENTS RATING STARS */}
                {ratingsDisplay(patientRatingStarsRef)}
                {/* COST INDICATOR */}
                {costDisplay()}
                {/* VIRTUAL VISIT INDICATOR IN MED-SM SCREEN*/}
                {isGroupedLocationDrawer &&
                  notXlScreen &&
                  virtualVisitDisplay()}
                {callAndScheduleDisplay()}
              </Flex>
            )}
          </Flex>
        </Card.Section>
        {!expandedProviderListView && (
          <Card.Section
            css={{ ...primarySecondaryContainerStyle() }}
            data-testid="data-card-details-secondary-section"
          >
            {showInternalCardInfo && (
              <Flex
                css={{
                  '& .abyss-popover-trigger': {
                    marginLeft: '0px !important',
                  },
                  '& .abyss-popover-trigger > div': {
                    padding: 0,
                    background: 'transparent !important',
                    marginLeft: 0,
                    justifyContent: carddInfoStyles()?.justifyContent,
                  },
                  margin: carddInfoStyles()?.margin,
                }}
                data-testid="data-card-card-info"
              >
                {isUmr && (
                  <CardInfoForUMR
                    cardType="snack-card-map-popup-internal"
                    displayMiddle={true}
                    displayTop={false}
                    headers={headers}
                    indexForAnalytics={getLinkPositionIndex(index)}
                    isSmartChoiceFFEnabled={showInternalCardInfo}
                    landingPage={false}
                    locationForAnalytics={linkLocationName}
                    onClosePopup={() => setIsOpen(false)}
                    popupContainer={popupContainer}
                    practitioner={practitioner}
                    searchTerm={searchTerm}
                    selectedFilters={selectedFilters}
                  />
                )}
                {!isUmr && (
                  <CardInfo
                    cardType={badgeCardType}
                    headers={headers}
                    indexForAnalytics={getLinkPositionIndex(index)}
                    isSmartChoiceFFEnabled={showInternalCardInfo}
                    landingPage={false}
                    locationForAnalytics={linkLocationName}
                    onClosePopup={() => setIsOpen(false)}
                    popupContainer={popupContainer}
                    practitioner={practitioner}
                    searchTerm={searchTerm}
                    selectedFilters={selectedFilters}
                  />
                )}
              </Flex>
            )}
            <Flex justify={getFlexJustification(popupContainer)}>
              <Flex direction="column">
                {enablePatientView && !expandedProviderListView && (
                  <Flex
                    alignItems="center"
                    css={{
                      'abyss-flex-root': {
                        cursor: shouldShowCursorOnRatings(healthGradeRating),
                      },
                    }}
                    data-testid="data-card-clickable-reviews"
                    onClick={ratingsOnClickHandler(
                      healthGradeRating,
                      handleDetailsOnClick
                    )}
                  >
                    {sectionTypeDerived === Constants.RESULT_SECTION.PROVIDER &&
                      !suppressPatientReviews && (
                        <ProviderRating
                          data-testid={`data-card-rating-${sectionTypeDerived}`}
                          enableMapViewHideOption={enableMapViewHideOption}
                          expandedProviderListView={expandedProviderListView}
                          healthGradeRating={healthGradeRating}
                          popupContainer={popupContainer}
                          providerId={providerId}
                          sectionTypeDerived={sectionTypeDerived}
                          size={valCheck(mobileScreen, 12, 13)}
                          surveyCount={practitioner?.surveyCount}
                          underline={false}
                        />
                      )}
                  </Flex>
                )}
                {virtualVisitDisplay()}
              </Flex>
              <Flex css={FlexStyles(popupContainer, expandedProviderListView)}>
                {!enablePatientView && !expandedProviderListView && (
                  <Flex
                    css={{
                      'abyss-flex-root': {
                        cursor: valCheck(
                          healthGradeRating !== null,
                          'pointer',
                          ''
                        ),
                      },
                    }}
                    data-testid="data-card-clickable-reviews"
                    onClick={
                      healthGradeRating !== null
                        ? handleDetailsOnClick
                        : undefined
                    }
                  >
                    {sectionTypeDerived === Constants.RESULT_SECTION.PROVIDER &&
                      !suppressPatientReviews && (
                        <ProviderRating
                          data-testid="data-card-rating-Provider"
                          enableMapViewHideOption={enableMapViewHideOption}
                          expandedProviderListView={expandedProviderListView}
                          healthGradeRating={healthGradeRating}
                          providerId={providerId}
                          sectionTypeDerived={sectionTypeDerived}
                          size={getTextSize(enablePatientView)}
                          surveyCount={practitioner?.surveyCount}
                          underline={false}
                        />
                      )}
                  </Flex>
                )}
                {enableCost &&
                  portalShowCost &&
                  !popupContainer &&
                  practitioner?.costIndicator &&
                  !suppressCostEstimates &&
                  !expandedProviderListView &&
                  smartChoicePriceCheck && (
                    <Flex css={costIndicatorStyles(enableMapViewHideOption)}>
                      <CostIndicatorPopover
                        callFrom="datacard"
                        costIndicator={practitioner.costIndicator}
                        enableMapViewHideOption={enableMapViewHideOption}
                        headers={headers}
                        indexForAnalytics={getLinkPositionIndex(index)}
                        locationForAnalytics={linkLocationName}
                        practitioner={practitioner}
                        providerType={providerType}
                        searchTerm={searchTerm}
                        selectedFilters={selectedFilters}
                      />
                    </Flex>
                  )}
              </Flex>
            </Flex>
          </Card.Section>
        )}

        {!shouldShowVirtualVisitAddress &&
          (!ctaDisplayLogic &&
          !expandedProviderListView &&
          !suppressAddressAndMiles ? (
            <MilesAndAddressWrapper
              data-auto-testid="datacard-address"
              data-testid="datacard-address"
            >
              <IconMaterial
                data-auto-testid={`data-card-directions-icon-${sectionTypeDerived}-${providerId}`}
                data-testid={`data-card-directions-icon-${sectionTypeDerived}-${providerId}`}
                icon="directions"
                size={valCheck(popupContainer, 18, 24)}
                style={{
                  marginTop: 0,
                  marginRight: valCheck(popupContainer, 0, 4),
                }}
              />
              {!popupContainer && (
                <Button
                  aria-labelledby={`LocName${providerId} LocAdrs${providerId}`}
                  css={CardButtonStyle}
                  data-auto-testid={`data-card-location-address-${address}-${providerId}`}
                  data-testid={`data-card-location-address-${address}-${providerId}`}
                  onClick={onCardClick}
                  onKeyDown={onCardClick}
                  role="button"
                  variant="ghost"
                >
                  <Address
                    address={address}
                    badgeCardType={badgeCardType}
                    expandedProviderListView={expandedProviderListView}
                    id={providerId}
                    isCTADisplayLogic={ctaDisplayLogic}
                    isGroupedLocationDrawer={isGroupedLocationDrawer}
                    sectionType={sectionTypeDerived}
                  />
                </Button>
              )}
              <Flex
                aria-labelledby={`LocName${providerId} LocAdrs${providerId} LocNP${providerId}`}
                css={CardButtonStyle}
                data-testid="interactive-element"
                onClick={onCardClick}
              >
                <IconMaterial
                  data-auto-testid={`data-card-location-on-icon-${sectionTypeDerived}-${providerId}`}
                  data-testid={`data-card-location-on-icon-${sectionTypeDerived}-${providerId}`}
                  icon="location_on"
                  size={24}
                />
                <Flex css={milesTextStyles}>
                  <Miles
                    distance={distance || ''}
                    headers={headers}
                    id={providerId}
                    indexForAnalytics={getLinkPositionIndex(index)}
                    isCTADisplayLogic={ctaDisplayLogic}
                    isGroupedLocationDrawer={isGroupedLocationDrawer}
                    locationForAnalytics={linkLocationName}
                    onClosePopup={() => setIsOpen(false)}
                    popupContainer={popupContainer}
                    practitioner={practitioner}
                    providerType={providerType}
                    searchTerm={searchTerm}
                    sectionType={sectionTypeDerived}
                    selectedFilters={selectedFilters}
                    showBestMatchDetails={showBestMatchDetails}
                    showSmartChoice={showSmartChoice}
                  />
                </Flex>
              </Flex>
            </MilesAndAddressWrapper>
          ) : (
            <MilesAndAddressWrapper
              css={
                !expandedProviderListView
                  ? {
                      cursor: 'pointer',
                      gap: mileAndAddressGap(showSmartChoice),
                    }
                  : { padding: '0px', gap: '0px' }
              }
            >
              {!expandedProviderListView && !suppressAddressAndMiles && (
                <>
                  <Button
                    aria-labelledby={`LocName${providerId} LocAdrs${providerId}`}
                    css={CardButtonStyle}
                    data-auto-testid="datacard-address"
                    data-testid="datacard-address"
                    onClick={() => {
                      handleAdobeSearchResultClickEvent('address');
                      handleNavigationOnClick();
                    }}
                    role="button"
                    variant="ghost"
                  >
                    <IconMaterial
                      data-auto-testid={`data-card-directions-icon-${sectionTypeDerived}-${providerId}`}
                      data-testid={`data-card-directions-icon-${sectionTypeDerived}-${providerId}`}
                      icon="directions"
                      size={valCheck(popupContainer, 18, 24)}
                      style={{
                        marginTop: 0,
                        marginRight: valCheck(popupContainer, 0, 4),
                      }}
                    />
                    {!popupContainer && (
                      <>
                        <Address
                          address={address}
                          badgeCardType={badgeCardType}
                          enableMapViewHideOption={enableMapViewHideOption}
                          expandedProviderListView={expandedProviderListView}
                          id={providerId}
                          isCTADisplayLogic={ctaDisplayLogic}
                          isGroupedLocationDrawer={isGroupedLocationDrawer}
                          sectionType={sectionTypeDerived}
                          showSmartChoice={showSmartChoice}
                        />
                        <VoiceOnlyText>
                          {t('DIRECTIONS_FOR')} {name}
                        </VoiceOnlyText>
                      </>
                    )}
                  </Button>
                  <div
                    aria-labelledby={`LocMiles${providerId}`}
                    data-auto-testid={`data-card-location-address-${address}-${providerId}`}
                    data-testid={`data-card-location-address-${address}-${providerId}`}
                  >
                    <Miles
                      distance={distance || ''}
                      headers={headers}
                      id={providerId}
                      indexForAnalytics={getLinkPositionIndex(index)}
                      isCTADisplayLogic={ctaDisplayLogic}
                      isGroupedLocationDrawer={isGroupedLocationDrawer}
                      locationForAnalytics={linkLocationName}
                      onClosePopup={() => setIsOpen(false)}
                      onMilesClick={onCardClick}
                      popupContainer={popupContainer}
                      practitioner={practitioner}
                      providerType={providerType}
                      searchTerm={searchTerm}
                      sectionType={sectionTypeDerived}
                      selectedFilters={selectedFilters}
                      showBestMatchDetails={showBestMatchDetails}
                      showSmartChoice={showSmartChoice}
                    />
                  </div>
                </>
              )}
              {showSmartChoice && !expandedProviderListView && (
                <Button
                  css={CardButtonStyle}
                  data-auto-testid="datacard-smart-choice"
                  data-testid="datacard-smart-choice"
                  onClick={() => {
                    handleSmartChoiceClick && handleSmartChoiceClick();
                  }}
                  role="button"
                  variant="ghost"
                >
                  <Text>{'SC'}</Text>
                </Button>
              )}
            </MilesAndAddressWrapper>
          ))}
        {!shouldShowVirtualVisitAddress &&
          popupContainer &&
          !suppressAddressAndMiles && (
            <Address
              address={address}
              badgeCardType={badgeCardType}
              enableMapViewHideOption={enableMapViewHideOption}
              expandedProviderListView={expandedProviderListView}
              id={providerId}
              isCTADisplayLogic={ctaDisplayLogic}
              isGroupedLocationDrawer={isGroupedLocationDrawer}
              popupContainer={popupContainer}
              sectionType={sectionTypeDerived}
              showSmartChoice={showSmartChoice}
            />
          )}
        {!expandedProviderListView && callAndScheduleDisplay()}
      </CardContainer>
    </>
  );
};
